<div class="flex-left-column light-control-host" data-cy="conclude-test-dialog">
  <div class="modal-header">
    <h3 class="panel-title" data-cy="dialog-title">{{ dialogTitle }}</h3>
    <button
      type="button"
      class="modal-close-btn"
      (click)="onCancelClick()"
      pTooltip="Close"
      tooltipPosition="left"
      aria-label="Close"
    >
      <i class="fa-utility fa-semibold fa-xmark"></i>
    </button>
  </div>

  <div class="modal-subtitle">
    Concluding a test disposes of resources in the recovery region and returns the application to a standby state. Do
    not proceed until you no longer need these resources.
  </div>

  <div class="modal-step">
    <div class="modal-step-label">
      <span class="modal-step-number"></span>
      <span class="modal-step-title"> Applications in {{ !!application ? application.targetRegion : '' }} </span>
      @if (applications.length > 0 || hasAnyInProgressCandidate) {
        <div class="modal-step-actions">
          @if (hasAnyInProgressCandidate) {
            <label class="show-only-completed-toggle">
              <input
                class="arpio-checkbox"
                type="checkbox"
                data-cy="show-only-completed-toggle"
                [(ngModel)]="showOnlyCompleted"
              />
              Show only completed
            </label>
          }
          @if (applications.length > 0) {
            <span class="selection-count">
              <strong>{{ selectedApplications.length }}</strong> of {{ applications.length }} selected
            </span>
            <button
              type="button"
              class="select-all-link"
              (click)="toggleSelectAllApplications(!areAllApplicationsSelected())"
            >
              {{ areAllApplicationsSelected() ? 'Deselect all' : 'Select all' }}
            </button>
          }
        </div>
      }
    </div>
    <!-- "Show only completed" can hide every row when nothing has finished restoring; keep the
           toggle reachable above and surface a hint here so the user isn't stranded. -->
    @if (applications.length === 0 && showOnlyCompleted && hasAnyInProgressCandidate) {
      <div class="app-selection-empty" data-cy="no-completed-empty-state">
        <i class="fa-solid fa-circle-info"></i>
        <span
          >No applications have finished restoring yet. Uncheck “Show only completed” to act on the in-progress
          applications.</span
        >
      </div>
    }
    @if (applications.length > 0) {
      <div id="applicationSelectionList" class="app-selection-list">
        @for (application of applications; track applicationTrackBy($index, application)) {
          <label
            class="app-selection-row"
            [class.selected]="applicationIsChecked(application.applicationObj)"
            [attr.data-cy]="'app-row-' + application.applicationObj.applicationId"
          >
            <input
              class="arpio-checkbox"
              type="checkbox"
              (input)="toggleApplicationSelection(application.applicationObj.applicationId)"
              [checked]="applicationIsChecked(application.applicationObj)"
            />
            <span class="app-name">{{ application.applicationObj.name }}</span>
            <span
              class="app-restore-status"
              data-cy="app-restore-status"
              [class.app-restore-status--in-progress]="statusInfoFor(application.applicationObj).inProgress"
            >
              @if (statusInfoFor(application.applicationObj).inProgress) {
                <i class="fa-solid fa-spinner fa-spin" aria-hidden="true"></i>
              }
              {{ statusInfoFor(application.applicationObj).label }}
            </span>
          </label>
        }
      </div>
    }
  </div>

  @if (hasMixedSelection) {
    <div class="notice-warning conclude-disclaimer" data-cy="mixed-disclaimer">
      <i class="fa-utility fa-semibold fa-circle-exclamation"></i>
      <p>
        <strong>{{ cancelledAppNames }}</strong>
        {{ selectedToCancel.length === 1 ? 'is' : 'are' }} still mid-restore and will be cancelled. The test outcome
        below applies only to <strong>{{ concludedAppNames }}</strong
        >.
      </p>
    </div>
  }

  @if (allSelectedWillBeCancelled && selectedApplications.length > 0) {
    <div class="notice-warning conclude-disclaimer" data-cy="all-cancel-disclaimer">
      <i class="fa-utility fa-semibold fa-circle-exclamation"></i>
      <p>
        <strong>{{ cancelledAppNames }}</strong>
        {{ selectedToCancel.length === 1 ? 'is' : 'are' }} still in
        {{ selectedToCancel.length === 1 ? 'its' : 'their' }} restore. Submitting will cancel
        {{ selectedToCancel.length === 1 ? 'this test' : 'these tests' }} and return the
        {{ selectedToCancel.length === 1 ? 'application' : 'applications' }} to standby — no test outcome will be
        recorded.
      </p>
    </div>
  }

  @if (outcomeRequired) {
    <div class="modal-step">
      <div class="modal-step-label">
        <span class="modal-step-number"></span>
        <span class="modal-step-title">Test outcome</span>
      </div>
      <div class="outcome-row">
        <label class="outcome-label" for="successful-test-select">Was this test successful?</label>
        <select
          id="successful-test-select"
          class="arpio-select"
          data-cy="test-outcome-select"
          [(ngModel)]="successfulTest"
        >
          <option value="" disabled hidden>Select outcome...</option>
          <option value="true">Yes</option>
          <option value="false">No</option>
        </select>
      </div>
      <textarea
        class="outcome-notes"
        data-cy="test-notes-textarea"
        placeholder="Notes"
        [(ngModel)]="testNotes"
        rows="3"
      ></textarea>
    </div>
  }

  @if (protectRecoveryPointEnabled) {
    <div class="modal-step">
      <div class="modal-step-label">
        <span class="modal-step-number"></span>
        <span class="modal-step-title">Retention</span>
      </div>
      <label class="labeled-checkbox" for="protect-rps">
        <input id="protect-rps" class="arpio-checkbox" type="checkbox" [(ngModel)]="protectRecoveryPoints" />
        <span class="labeled-checkbox-content">
          <span class="labeled-checkbox-title">Retain Recovery Points indefinitely</span>
          <span class="modal-subtitle">
            Normally, Recovery Points are deleted based on your retention policy. Checking this box ensures they are
            retained until manually removed. You can manage retained Recovery Points on the
            <a href="{{ retentionPolicyUrl }}" target="_blank">Retention Policy</a> screen.
          </span>
        </span>
      </label>
    </div>
  }

  @if (outcomeRequired && recoveryReportsEnabled) {
    <div class="modal-step">
      <div class="modal-step-label">
        <span class="modal-step-number"></span>
        <span class="modal-step-title">Generate report</span>
      </div>
      <label class="labeled-checkbox" for="generate-recovery-report">
        <input
          id="generate-recovery-report"
          class="arpio-checkbox"
          type="checkbox"
          data-cy="generate-recovery-report-checkbox"
          [(ngModel)]="generateRecoveryReport"
        />
        <span class="labeled-checkbox-content">
          <span class="labeled-checkbox-title">Generate a report</span>
          <span class="modal-subtitle">
            Generates a signed PDF Recovery Test Report for the concluded applications when the test is concluded.
          </span>
        </span>
      </label>
      @if (generateRecoveryReport) {
        <div class="recipients-field" data-cy="recovery-report-recipients-field">
          <label class="recipients-label" for="recovery-report-recipients">Send to</label>
          <string-list-control
            id="recovery-report-recipients"
            data-cy="recovery-report-recipients"
            [validationFunction]="isValidEmail"
            [(ngModel)]="reportRecipients"
          ></string-list-control>
          <span class="recipients-helper">Recipients will receive the PDF report when the test is concluded.</span>
        </div>
      }
    </div>
  }

  <div class="modal-footer">
    <button
      class="arpio-secondary-button"
      data-cy="dialog-cancel-button"
      (click)="onCancelClick()"
      [disabled]="launching"
    >
      CANCEL
    </button>
    <!-- Two static-label buttons (only one renders) instead of one dynamic label: busy-button
           snapshots its content on init, so a label that flips after open would otherwise stay stale. -->
    @if (allSelectedWillBeCancelled) {
      <button
        busy-button
        [busy]="launching"
        id="conclude-button"
        data-cy="submit-button"
        class="arpio-button"
        (click)="onConcludeClick()"
        [disabled]="!canSubmit || launching"
      >
        CANCEL TEST
      </button>
    }
    @if (!allSelectedWillBeCancelled) {
      <button
        busy-button
        [busy]="launching"
        id="conclude-button"
        data-cy="submit-button"
        class="arpio-button"
        (click)="onConcludeClick()"
        [disabled]="!canSubmit || launching"
      >
        CONCLUDE TEST
      </button>
    }
  </div>
</div>
